import {
  HasuraDataTableColumnDef,
  TablePageLayout,
  DataTableEx,
  DataTableExRef,
  ConfigurationContext,
  TranslationInput,
} from '@kirz/mui-admin';
import AddIcon from '@mui/icons-material/Add';
import { Button } from '@mui/material';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

export function VideoLanes() {
  const tableRef = useRef<DataTableExRef>(null);
  const { hasura } = useContext(ConfigurationContext);
  const [sortValues, setSortValues] = useState<number[] | null>(null);

  const columns = useMemo<HasuraDataTableColumnDef[]>(
    () => [
      {
        field: 'serialNumber',
        type: 'sort',
        sortKey: 'serialNumber',
        rowsSortValues: sortValues,
        onSortChange: async (direction, row, targetRowSort) => {
          const source = 'VideoGroup';
          const { id } = row;

          await hasura.request({
            type: 'custom',
            query: `
            mutation ChangeSort($where1: ${source}BoolExp!, $set1: ${source}SetInput!, $where2: ${source}BoolExp!, $set2: ${source}SetInput!) {
              result2: update${source}(where: $where2, _set: $set2) {
                affected_rows
              }
              result1: update${source}(where: $where1, _set: $set1) {
                affected_rows
              }
            }
          `,
            variables: {
              where1: { id: { _eq: id } },
              set1: { serialNumber: targetRowSort },
              where2: { serialNumber: { _eq: targetRowSort } },
              set2: { serialNumber: row.serialNumber },
            },
          });

          tableRef.current?.reload();
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        type: 'translation',
        flex: 1,
      },
      {
        field: 'itemsAggregate { aggregate { *count* } }',
        type: 'relationship',
        headerName: 'Number of videos',
        flex: 1,
        fetchRemoved: false,
      },
      {
        field: 'isActive',
        headerName: 'Active',
        type: 'boolean',
        sortable: false,
      },
    ],
    [sortValues],
  );

  useEffect(() => {
    (async () => {
      const items = await hasura.request({
        type: 'query',
        source: 'videoGroup',
        selection: 'serialNumber',
      });

      setSortValues(items.map((x: any) => x.serialNumber));
    })();
  }, []);

  return (
    <TablePageLayout
      title="Video lanes"
      actionContent={
        <Button
          sx={{ ml: 'auto' }}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            tableRef.current?.openFormDialog();
          }}
        >
          Add new
        </Button>
      }
    >
      <DataTableEx
        id="categories-table"
        ref={tableRef}
        source="videoGroup"
        columns={columns}
        editPageUrl="/video-lanes"
        deletable={{
          onDeleted: (row) => {
            setSortValues(sortValues!.filter((x) => x !== row.serialNumber));
          },
        }}
        sortBy={{ field: 'serialNumber', sort: 'asc' }}
        persistStateMode="query"
        formDialogProps={{
          autoFocus: false,
          formProps: {
            defaultValues: {
              isActive: false,
            },
          },
        }}
        searchFilter={{
          inputProps: {
            placeholder: 'Search by name',
          },
          filter: (search) => ({
            _or: search.flatMap((str) => [
              { name: { _cast: { String: { _ilike: `%${str}%` } } } },
            ]),
          }),
        }}
        customFilter={{
          filters: [
            {
              type: 'boolean',
              name: 'Is active',
              field: 'isActive',
            },
          ],
        }}
        formTitle={(isNew) => (isNew ? 'New video lane' : 'Edit video lane')}
      >
        <TranslationInput
          name="name"
          label="Name"
          required
          gridCols={{ md: 6, lg: 6, xl: 6 }}
        />
      </DataTableEx>
    </TablePageLayout>
  );
}
